import { supabase } from "@/integrations/supabase/client";
import type { 
  PlaybookSectionConfig, 
  PlaybookTemplateTask, 
  CreateTemplateTaskInput,
  UpdateTemplateTaskInput,
  UpdateSectionInput,
  CreateSectionInput
} from "@/types/Admin";

export const adminService = {
  // Section operations
  async getSections(): Promise<PlaybookSectionConfig[]> {
    const { data, error } = await supabase
      .from('playbook_sections')
      .select('*')
      .order('sort_order');
    
    if (error) throw error;
    return data || [];
  },

  async createSection(input: CreateSectionInput): Promise<PlaybookSectionConfig> {
    const { data, error } = await supabase
      .from('playbook_sections')
      .insert({
        section_key: input.section_key,
        section_label: input.section_label,
        sort_order: input.sort_order,
        is_active: input.is_active ?? true,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateSection(id: string, updates: UpdateSectionInput): Promise<PlaybookSectionConfig> {
    const { data, error } = await supabase
      .from('playbook_sections')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateSectionOrder(sections: { id: string; sort_order: number }[]): Promise<void> {
    const updates = sections.map(({ id, sort_order }) =>
      supabase
        .from('playbook_sections')
        .update({ sort_order })
        .eq('id', id)
    );
    
    const results = await Promise.all(updates);
    const failed = results.find(r => r.error);
    if (failed?.error) throw failed.error;
  },

  // Template task operations
  async getTemplateTasks(): Promise<PlaybookTemplateTask[]> {
    const { data, error } = await supabase
      .from('playbook_templates')
      .select('*')
      .order('sort_order');
    
    if (error) throw error;
    return (data || []).map(task => ({
      ...task,
      depends_on: task.depends_on || [],
      alternative_actions: Array.isArray(task.alternative_actions) 
        ? (task.alternative_actions as { label: string; path: string }[]) 
        : []
    }));
  },

  async createTemplateTask(input: CreateTemplateTaskInput): Promise<PlaybookTemplateTask> {
    const { data, error } = await supabase
      .from('playbook_templates')
      .insert({
        task_key: input.task_key,
        task_name: input.task_name,
        task_description: input.task_description || null,
        section_key: input.section_key,
        sort_order: input.sort_order,
        is_required: input.is_required || false,
        is_blocker: input.is_blocker || false,
        action_path: input.action_path || null,
        action_label: input.action_label || null,
        depends_on: input.depends_on || [],
        auto_complete_check: input.auto_complete_check || null
      })
      .select()
      .single();
    
    if (error) throw error;
    return {
      ...data,
      depends_on: data.depends_on || [],
      alternative_actions: Array.isArray(data.alternative_actions) 
        ? (data.alternative_actions as { label: string; path: string }[]) 
        : []
    };
  },

  async updateTemplateTask(id: string, updates: UpdateTemplateTaskInput): Promise<PlaybookTemplateTask> {
    const { data, error } = await supabase
      .from('playbook_templates')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return {
      ...data,
      depends_on: data.depends_on || [],
      alternative_actions: Array.isArray(data.alternative_actions) 
        ? (data.alternative_actions as { label: string; path: string }[]) 
        : []
    };
  },

  async deleteTemplateTask(id: string): Promise<void> {
    const { error } = await supabase
      .from('playbook_templates')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async updateTaskOrder(tasks: { id: string; section_key: string; sort_order: number }[]): Promise<void> {
    const updates = tasks.map(({ id, section_key, sort_order }) =>
      supabase
        .from('playbook_templates')
        .update({ section_key, sort_order })
        .eq('id', id)
    );
    
    const results = await Promise.all(updates);
    const failed = results.find(r => r.error);
    if (failed?.error) throw failed.error;
  }
};
